import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import PageHeader from '@/components/feature/PageHeader';
import { Pill } from '@/components/base/Badges';
import { serviceTypeMeta, type PackageSubscriber } from '@/mocks/packages';
import { useAppData } from '@/store/AppDataContext';

type FilterType = 'all' | 'active' | 'expired';

function ProgressBar({ remaining, total }: { remaining: number; total: number }) {
  const pct = total > 0 ? Math.round((remaining / total) * 100) : 0;
  const colorClass = pct > 50 ? 'bg-success-500' : pct > 20 ? 'bg-warning-500' : 'bg-danger-500';
  return (
    <div className="mt-1.5 w-full">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-[11px] text-foreground-400">{remaining} / {total} قطعة</span>
        <span className={`text-[11px] font-medium ${pct > 50 ? 'text-success-600' : pct > 20 ? 'text-warning-600' : 'text-danger-600'}`}>
          {pct}٪ متبقية
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-background-200">
        <div className={`h-full rounded-full transition-all duration-500 ${colorClass}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function SubscriberRow({ sub }: { sub: PackageSubscriber }) {
  const expired = sub.status === 'expired' || sub.remainingItems === 0;
  return (
    <div className="flex flex-col gap-2 rounded-card border border-background-100 bg-background-50 px-4 py-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-700 text-[15px] font-semibold">
            {sub.name.charAt(0)}
          </span>
          <div>
            <p className="font-semibold text-[14px] text-foreground-950 leading-tight">{sub.name}</p>
            <button
              type="button"
              onClick={() => navigator.clipboard.writeText(sub.phone).catch(() => null)}
              className="flex items-center gap-1 text-[12px] text-foreground-400 transition-colors hover:text-secondary-600"
              title="اضغط لنسخ الرقم"
            >
              <i className="ri-phone-line text-[12px]" aria-hidden="true"></i>
              {sub.phone}
              <i className="ri-file-copy-line text-[11px] opacity-60" aria-hidden="true"></i>
            </button>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {sub.status === 'expired' ? (
            <Pill tone="danger">منتهي الصلاحية</Pill>
          ) : sub.remainingItems === 0 ? (
            <Pill tone="warning">نفد الرصيد</Pill>
          ) : (
            <Pill tone="success">نشط</Pill>
          )}
          <span className="text-[11px] text-foreground-400">ينتهي {sub.expiryDate}</span>
        </div>
      </div>
      <ProgressBar remaining={sub.remainingItems} total={sub.totalItems} />
      <div className="flex items-center justify-between gap-2 border-t border-background-100 pt-2">
        <span className="flex items-center gap-1.5 text-[11px] text-foreground-400">
          <i className="ri-calendar-line text-[12px]" aria-hidden="true"></i>
          اشترك في {sub.subscribeDate}
        </span>
        {!expired && sub.remainingItems < sub.totalItems * 0.25 && (
          <span className="flex items-center gap-1 text-[11px] text-warning-700">
            <i className="ri-alarm-warning-line text-[12px]" aria-hidden="true"></i>
            الرصيد يكاد ينفد
          </span>
        )}
      </div>
    </div>
  );
}

export default function SubscribersPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { packages } = useAppData();
  const pkg = packages.find((p) => p.id === id);

  const [filter, setFilter] = useState<FilterType>('all');
  const [search, setSearch] = useState('');

  if (!pkg) {
    return (
      <DashboardLayout title="المشتركون">
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <i className="ri-box-3-line text-5xl text-foreground-300" aria-hidden="true"></i>
          <p className="mt-4 text-foreground-500">لم يتم العثور على الباقة</p>
          <button
            type="button"
            onClick={() => navigate('/packages')}
            className="mt-4 text-[13px] font-medium text-secondary-600 hover:text-secondary-700"
          >
            العودة للباقات
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const meta = serviceTypeMeta[pkg.serviceType];
  const activeCount = pkg.subscribers.filter((sub) => sub.status === 'active' && sub.remainingItems > 0).length;

  const filtered = pkg.subscribers.filter((sub) => {
    const matchFilter =
      filter === 'all' ||
      (filter === 'active' && sub.status === 'active' && sub.remainingItems > 0) ||
      (filter === 'expired' && (sub.status === 'expired' || sub.remainingItems === 0));
    const matchSearch =
      !search.trim() || sub.name.includes(search.trim()) || sub.phone.includes(search.trim());
    return matchFilter && matchSearch;
  });

  return (
    <DashboardLayout title={`مشتركو ${pkg.name}`}>
      <div className="mx-auto w-full max-w-[680px]">
        <PageHeader title={`مشتركو ${pkg.name}`} backTo="/packages" />

        <div className="mb-4 rounded-card border border-background-200 bg-white p-4 shadow-card">
          <div className="flex items-center gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[10px] bg-secondary-100 text-secondary-600">
              <i className={`${meta.icon} text-2xl`} aria-hidden="true"></i>
            </span>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-[15px] text-foreground-950">{pkg.name}</h3>
              <p className="mt-0.5 text-[12px] text-foreground-400">
                {pkg.itemCount} قطعة • {pkg.validityDays} يوم • {pkg.price} ر.س
              </p>
            </div>
            <div className="flex flex-col items-end shrink-0">
              <span className="font-bold text-[18px] text-foreground-950">{pkg.subscribers.length}</span>
              <span className="text-[11px] text-foreground-400">إجمالي المشتركين</span>
              <span className="text-[11px] text-success-600">{activeCount} نشط</span>
            </div>
          </div>
        </div>

        <div className="rounded-card border border-background-200 bg-white shadow-card">
          <div className="space-y-3 border-b border-background-100 p-4">
            <div className="relative">
              <i className="ri-search-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base" aria-hidden="true"></i>
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="ابحث بالاسم أو رقم الهاتف..."
                className="h-10 w-full rounded-field border border-background-300 bg-white pr-9 pl-4 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-secondary-400 focus:outline-none focus:ring-2 focus:ring-secondary-100"
              />
            </div>
            <div className="flex gap-1.5">
              {(['all', 'active', 'expired'] as FilterType[]).map((f) => {
                const labels: Record<FilterType, string> = { all: 'الكل', active: 'نشط', expired: 'منتهي/نفد' };
                return (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setFilter(f)}
                    className={[
                      'flex-1 rounded-btn py-1.5 text-[12px] font-medium transition-colors',
                      filter === f
                        ? 'bg-primary-500 text-white'
                        : 'border border-background-200 bg-white text-foreground-500 hover:bg-background-50',
                    ].join(' ')}
                  >
                    {labels[f]}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="p-4">
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-team-line text-2xl" aria-hidden="true"></i>
                </span>
                <p className="mt-3 font-semibold text-[14px] text-foreground-700">لا توجد نتائج</p>
                <p className="mt-1 text-[12px] text-foreground-400">
                  {search ? 'جرب كلمة بحث مختلفة' : 'لا يوجد مشتركون في هذه الفئة'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map((sub) => (
                  <SubscriberRow key={sub.id} sub={sub} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}